// Central catalogue of branding / printing services.
// Used by the home Services preview, the /services listing and /services/:slug detail pages.

// Service imagery, pre-sized by `node scripts/optimize-service-images.js`.
// The originals are camera-resolution (up to 5.3 MB) — far too heavy for the
// card grids, which render them at ~300px. Each service therefore carries a
// 1400px `image` for its detail hero and a 600px `thumb` for card grids.
import uvImg from "../assets/services/optimized/uv-printing.webp";
import uvThumb from "../assets/services/optimized/uv-printing-thumb.webp";
import uv360Img from "../assets/services/optimized/360-uv-printing.webp";
import uv360Thumb from "../assets/services/optimized/360-uv-printing-thumb.webp";
import screenImg from "../assets/services/optimized/screen-printing.webp";
import screenThumb from "../assets/services/optimized/screen-printing-thumb.webp";
import dtfImg from "../assets/services/optimized/digital-dtf-printing.webp";
import dtfThumb from "../assets/services/optimized/digital-dtf-printing-thumb.webp";
import digitalImg from "../assets/services/optimized/digital-printing.webp";
import digitalThumb from "../assets/services/optimized/digital-printing-thumb.webp";
import heatImg from "../assets/services/optimized/heat-transfer-vinyl.webp";
import heatThumb from "../assets/services/optimized/heat-transfer-vinyl-thumb.webp";
import sublimationImg from "../assets/services/optimized/sublimation-printing.webp";
import sublimationThumb from "../assets/services/optimized/sublimation-printing-thumb.webp";
import mugSubImg from "../assets/services/optimized/mug-sublimation-printing.webp";
import mugSubThumb from "../assets/services/optimized/mug-sublimation-printing-thumb.webp";
import laserImg from "../assets/services/optimized/laser-engraving.webp";
import laserThumb from "../assets/services/optimized/laser-engraving-thumb.webp";
import laserMarkImg from "../assets/services/optimized/laser-marking.webp";
import laserMarkThumb from "../assets/services/optimized/laser-marking-thumb.webp";
import embroideryImg from "../assets/services/optimized/embroidery.webp";
import embroideryThumb from "../assets/services/optimized/embroidery-thumb.webp";
import embossDebossImg from "../assets/services/optimized/embossing-debossing.webp";
import embossDebossThumb from "../assets/services/optimized/embossing-debossing-thumb.webp";
import idCardImg from "../assets/services/optimized/id-card-printing.webp";
import idCardThumb from "../assets/services/optimized/id-card-printing-thumb.webp";
import boxImg from "../assets/services/optimized/custom-box-printing.webp";
import boxThumb from "../assets/services/optimized/custom-box-printing-thumb.webp";
import threeDImg from "../assets/services/optimized/3d-printing.webp";
import threeDThumb from "../assets/services/optimized/3d-printing-thumb.webp";

export interface ServiceItem {
  slug: string;
  name: string;
  /** Short one-liner for cards. */
  tagline: string;
  /** Card / preview description. */
  short: string;
  /** Full intro paragraph for the detail page. */
  intro: string;
  /** 1400px hero image for the /services/:slug detail page. */
  image: string;
  /** 600px copy for card grids — never load the hero into a thumbnail. */
  thumb: string;
  /** react-icons name handled in the component via an icon map. */
  icon: string;
  /** Products / materials this method is best suited for. */
  bestFor: string[];
  /** Key advantages / selling points. */
  advantages: string[];
  /** Step-by-step production flow. */
  process: { title: string; description: string }[];
}

export const services: ServiceItem[] = [
  {
    slug: "uv-printing",
    name: "UV Printing",
    tagline: "Vivid, full-colour prints on almost any surface",
    short: "High-resolution UV-cured printing that bonds vibrant, scratch-resistant colour directly onto flat and curved products.",
    intro:
      "UV printing uses ultraviolet light to instantly cure ink as it is laid down, producing razor-sharp, full-colour graphics that resist fading and scratching. Because the ink dries on contact, we can print on materials that traditional methods struggle with — including metal, glass, acrylic, wood and plastics — making it our go-to method for premium corporate gifts.",
    image: uvImg,
    thumb: uvThumb,
    icon: "IoPrint",
    bestFor: ["Metal & steel bottles", "Power banks & tech gadgets", "Pens & metal items", "Acrylic & glass awards", "Wooden products", "Phone accessories"],
    advantages: [
      "Photographic, full-colour detail",
      "Prints on flat & curved surfaces",
      "Scratch & water resistant finish",
      "No drying time — fast turnaround",
      "Vibrant on dark and clear materials",
    ],
    process: [
      { title: "Artwork Setup", description: "We prepare and colour-match your logo for the UV print bed." },
      { title: "Jigging", description: "Items are fixed in custom jigs for perfect, repeatable alignment." },
      { title: "UV Print & Cure", description: "Ink is jetted and instantly cured under UV-LED lamps." },
      { title: "Quality Check", description: "Each piece is inspected for colour accuracy and finish." },
    ],
  },
  {
    slug: "360-uv-printing",
    name: "360° UV Printing",
    tagline: "Seamless wrap-around printing for bottles and tumblers",
    short: "Full-colour artwork wrapped continuously around drinkware and cylindrical gifts — no seam, no join.",
    intro:
      "A 360° UV printer rotates the product against the print head so your artwork wraps continuously around the entire item with no visible seam or join. It is the cleanest way to brand drinkware and cylindrical products in full colour, letting us run photographic designs, patterns and multi-colour logos all the way around a bottle, tumbler or flask — coverage no flat printing method can reach.",
    image: uv360Img,
    thumb: uv360Thumb,
    icon: "IoWaterOutline",
    bestFor: ["Water bottles & flasks", "Tumblers & travel mugs", "Cylindrical containers", "Full wrap-around artwork", "Photographic multi-colour designs", "Pens & slim barrels"],
    advantages: [
      "Continuous 360° coverage with no seam",
      "Unlimited colours and photographic detail",
      "Instantly cured — scratch & water resistant",
      "No screens or setup plates required",
      "Ideal for premium branded drinkware",
    ],
    process: [
      { title: "Artwork Wrap", description: "Your design is unwrapped to the exact circumference of the item." },
      { title: "Mounting", description: "The product is fitted onto the rotary spindle and aligned." },
      { title: "Rotary Print", description: "The item spins beneath the head as UV ink is jetted and cured." },
      { title: "Quality Check", description: "Each piece is checked for join accuracy and colour match." },
    ],
  },
  {
    slug: "screen-printing",
    name: "Screen Printing",
    tagline: "Bold, durable prints for high-volume orders",
    short: "The most cost-effective method for large quantities — thick, long-lasting ink layers with punchy solid colours.",
    intro:
      "Screen printing pushes ink through a fine mesh stencil, one colour at a time, to lay down a thick and extremely durable layer of colour. It is unbeatable for bulk orders of t-shirts, bags and bottles where a small number of bold spot colours need to look consistent across thousands of pieces at the lowest possible unit cost.",
    image: screenImg,
    thumb: screenThumb,
    icon: "IoBrushOutline",
    bestFor: ["T-shirts & apparel", "Tote & jute bags", "Drinkware", "Paper & gift bags", "Caps & headwear", "Promotional textiles"],
    advantages: [
      "Lowest cost per unit at volume",
      "Thick, opaque, long-lasting ink",
      "Brilliant solid spot colours",
      "Excellent on fabric & dark materials",
      "Pantone colour matching",
    ],
    process: [
      { title: "Colour Separation", description: "Your artwork is split into individual spot colours." },
      { title: "Screen Exposure", description: "A stencil is burned onto a mesh screen for each colour." },
      { title: "Printing", description: "Ink is pressed through the screen onto each item." },
      { title: "Curing", description: "Prints are heat-cured for a permanent, wash-proof finish." },
    ],
  },
  {
    slug: "digital-dtf-printing",
    name: "Digital & DTF Printing",
    tagline: "Photo-quality, full-colour, no minimums",
    short: "Direct-to-film and digital printing for complex, multi-colour artwork and gradients — perfect for small batches.",
    intro:
      "Digital and Direct-to-Film (DTF) printing reproduce unlimited colours, gradients and photographic detail without the setup of screens. A design is printed onto a transfer film, coated with adhesive and heat-pressed onto the product. This makes it ideal for detailed logos, full-colour artwork and low minimum-quantity runs.",
    image: dtfImg,
    thumb: dtfThumb,
    icon: "IoColorPalette",
    bestFor: ["Full-colour logos", "Photographic designs", "T-shirts & hoodies", "Small batch orders", "Sportswear", "Custom merchandise"],
    advantages: [
      "Unlimited colours & gradients",
      "No minimum order quantity",
      "Soft, stretchable finish",
      "Fine detail & photo reproduction",
      "Fast sampling & reorders",
    ],
    process: [
      { title: "Design Prep", description: "Artwork is colour-managed and sized for print." },
      { title: "Film Printing", description: "The design is printed and powdered with adhesive." },
      { title: "Heat Press", description: "The transfer is pressed onto the garment under heat & pressure." },
      { title: "Peel & Finish", description: "Film is peeled to reveal a durable, vibrant print." },
    ],
  },
  {
    slug: "digital-printing",
    name: "Digital Printing",
    tagline: "Fast, accurate short runs with variable data",
    short: "Plate-free printing for business cards, flyers and brochures — quick turnaround and personalisation down to a single sheet.",
    intro:
      "Digital printing goes straight from artwork to press with no plates to make, which makes it the quickest and most economical route for short runs and tight deadlines. Because every sheet can carry different content, it also handles variable data beautifully — personalised names, unique codes or individual addresses across an entire batch of business cards, flyers, brochures and event collateral.",
    image: digitalImg,
    thumb: digitalThumb,
    icon: "IoDocumentTextOutline",
    bestFor: ["Business cards", "Flyers & leaflets", "Brochures & catalogues", "Certificates & invitations", "Short runs & reprints", "Personalised variable data"],
    advantages: [
      "No plates — lowest setup cost",
      "Same-week turnaround on most jobs",
      "Economical from very small quantities",
      "Variable data and personalisation",
      "Hard-copy proofs before any bulk run",
    ],
    process: [
      { title: "File Prep", description: "Artwork is imposed, bleed-checked and colour-profiled for the press." },
      { title: "Proof", description: "A printed proof is produced for you to approve." },
      { title: "Print Run", description: "The job runs digitally, with variable data merged where needed." },
      { title: "Finishing", description: "Trimming, folding, lamination and binding as specified." },
    ],
  },
  {
    slug: "heat-transfer-vinyl",
    name: "Heat Transfer & Vinyl",
    tagline: "Crisp names, numbers and single-colour logos",
    short: "Precision-cut vinyl and heat-applied transfers for clean lettering, team kits and personalised items.",
    intro:
      "Heat transfer vinyl is precision-cut from coloured or specialty film (matte, gloss, metallic, reflective, flock) and heat-pressed onto the product. It delivers crisp, weld-clean edges and is the perfect choice for names, numbers, single-colour logos and personalised one-off pieces such as sports kits and uniforms.",
    image: heatImg,
    thumb: heatThumb,
    icon: "IoLayersOutline",
    bestFor: ["Sports jerseys", "Names & numbers", "Uniforms & workwear", "Caps & bags", "Personalised gifts", "Reflective safety wear"],
    advantages: [
      "Clean, sharp edges",
      "Metallic, neon & reflective finishes",
      "Great for personalisation",
      "Durable & wash-resistant",
      "Cost-effective for small runs",
    ],
    process: [
      { title: "Vector Cut", description: "Your design is cut from coloured vinyl film." },
      { title: "Weeding", description: "Excess vinyl is removed by hand, leaving the design." },
      { title: "Heat Application", description: "Vinyl is pressed onto the item with heat & pressure." },
      { title: "Cool Peel", description: "Backing is peeled for a smooth, bonded finish." },
    ],
  },
  {
    slug: "sublimation-printing",
    name: "Sublimation Printing",
    tagline: "Edge-to-edge, full-wrap colour that never peels",
    short: "Dye-sublimation infuses colour into the material itself for permanent, all-over photographic prints.",
    intro:
      "In sublimation, solid dye is turned into gas by heat and bonded permanently into polyester fabrics and specially-coated hard surfaces. Because the colour becomes part of the material, the print never cracks, peels or fades — and it can cover the entire product edge-to-edge with photographic, full-colour artwork.",
    image: sublimationImg,
    thumb: sublimationThumb,
    icon: "IoBulbOutline",
    bestFor: ["Mugs & ceramics", "Polyester apparel", "Mouse pads", "Flags & banners", "Lanyards", "Phone cases"],
    advantages: [
      "Permanent — won't peel or fade",
      "Full edge-to-edge coverage",
      "Photographic, unlimited colour",
      "Soft, breathable finish on fabric",
      "Ideal for all-over designs",
    ],
    process: [
      { title: "Print to Transfer", description: "Artwork is printed onto sublimation transfer paper." },
      { title: "Position", description: "The transfer is fixed onto the coated product or fabric." },
      { title: "Heat & Pressure", description: "High heat turns the dye to gas and bonds it into the surface." },
      { title: "Reveal", description: "A vibrant, permanent, full-colour print emerges." },
    ],
  },
  {
    slug: "mug-sublimation-printing",
    name: "Mug Sublimation Printing",
    tagline: "Dishwasher-safe full-colour printing on mugs and cups",
    short: "Photo-quality artwork bonded permanently into the mug coating — it will not scratch, fade or wash off.",
    intro:
      "Mug sublimation bonds full-colour artwork permanently into the coating of a ceramic mug, so the print is dishwasher and microwave safe and will not scratch off with daily use. We print standard, magic colour-changing, inner-colour and travel mugs, wrapping photographs, patterns and logos right around the body of the cup — a favourite for employee gifts, café branding and event giveaways across Dubai.",
    image: mugSubImg,
    thumb: mugSubThumb,
    icon: "IoCafeOutline",
    bestFor: ["Ceramic & coffee mugs", "Magic colour-changing mugs", "Inner-colour & handle-colour mugs", "Photo and gradient artwork", "Employee & client gifts", "Café and restaurant branding"],
    advantages: [
      "Dishwasher and microwave safe",
      "Full-colour photographic reproduction",
      "Wraps around the whole mug body",
      "Never cracks, peels or fades",
      "Viable from a single piece upwards",
    ],
    process: [
      { title: "Artwork Setup", description: "Your design is sized and mirrored to the mug wrap template." },
      { title: "Transfer Print", description: "The design is printed onto sublimation transfer paper." },
      { title: "Heat Press", description: "A mug press bonds the dye into the coating at high heat." },
      { title: "Cool & Inspect", description: "Mugs are cooled, wiped and checked for full-wrap coverage." },
    ],
  },
  {
    slug: "laser-engraving",
    name: "Laser Engraving",
    tagline: "Premium, permanent, ultra-precise marking",
    short: "A focused laser etches your logo into the material for an elegant, tactile and indelible finish.",
    intro:
      "Laser engraving removes a thin layer of material with a focused beam to leave a precise, permanent mark that can never rub off. The understated, tactile result feels genuinely premium — which is why it's the preferred branding for executive pens, steel bottles, wooden items and corporate awards.",
    image: laserImg,
    thumb: laserThumb,
    icon: "IoHammerOutline",
    bestFor: ["Metal pens", "Stainless steel bottles & flasks", "Wooden gifts", "Crystal & acrylic awards", "Leather items", "Power banks"],
    advantages: [
      "Permanent — never fades or rubs off",
      "Premium, tactile finish",
      "Extreme precision & fine detail",
      "No inks or consumables",
      "Elegant tone-on-tone branding",
    ],
    process: [
      { title: "Vector Prep", description: "Your logo is converted to a clean engraving path." },
      { title: "Alignment", description: "The item is positioned and focused under the laser." },
      { title: "Engraving", description: "The laser etches the design into the surface." },
      { title: "Finishing", description: "Items are cleaned and inspected for consistency." },
    ],
  },
  {
    slug: "laser-marking",
    name: "Laser Marking",
    tagline: "Ultra-fine permanent marking for codes and detail",
    short: "High-contrast surface marking for serial numbers, QR codes, barcodes and intricate line artwork.",
    intro:
      "Laser marking alters the surface of a material without cutting deeply into it, producing extremely fine, high-contrast detail. Where engraving is about depth and feel, marking is about precision — it is how we apply serial numbers, QR codes, barcodes and intricate line artwork onto tools, electronics, medical parts and technical components with permanent, repeatable accuracy.",
    image: laserMarkImg,
    thumb: laserMarkThumb,
    icon: "IoScanOutline",
    bestFor: ["Serial numbers & asset tags", "QR codes & barcodes", "Fine detailed line artwork", "Tools & hand equipment", "Electronics & technical parts", "Traceability marking"],
    advantages: [
      "Extremely fine, legible detail",
      "Permanent and tamper-evident",
      "No inks, solvents or consumables",
      "Every piece can be marked uniquely",
      "Works on metal, plastic and coated surfaces",
    ],
    process: [
      { title: "Data Prep", description: "Codes, serials or artwork are prepared, including sequential data." },
      { title: "Parameter Test", description: "Power and speed are dialled in on a sample of the material." },
      { title: "Marking", description: "The laser marks each part in a fixed jig for repeatable placement." },
      { title: "Verification", description: "Codes are scan-tested and the finish is inspected." },
    ],
  },
  {
    slug: "embroidery",
    name: "Embroidery",
    tagline: "Premium stitched logos with a textured feel",
    short: "Thread-stitched branding that adds a high-end, durable and professional look to apparel and headwear.",
    intro:
      "Embroidery stitches your logo directly into the fabric with coloured thread, creating a raised, textured and highly durable finish that instantly elevates apparel. It is the most premium way to brand caps, polos, jackets and bags, conveying quality and longevity that printed methods can't match.",
    image: embroideryImg,
    thumb: embroideryThumb,
    icon: "IoShirtOutline",
    bestFor: ["Caps & beanies", "Polo shirts & jackets", "Workwear & uniforms", "Backpacks & bags", "Towels", "Fleece & hoodies"],
    advantages: [
      "Premium, high-perceived value",
      "Extremely durable — won't fade",
      "Raised, textured finish",
      "Professional corporate look",
      "Thread colour-matching",
    ],
    process: [
      { title: "Digitising", description: "Your logo is converted into a stitch file." },
      { title: "Hooping", description: "The garment is secured in an embroidery frame." },
      { title: "Stitching", description: "Multi-needle machines stitch the design with precision." },
      { title: "Trimming", description: "Threads are trimmed and the piece is pressed & checked." },
    ],
  },
  {
    // Embossing and debossing are the same press operation with the die
    // reversed, so the client sells them as one service rather than two.
    slug: "embossing-debossing",
    name: "Embossing & Debossing",
    tagline: "Tactile branding pressed into — or raised out of — the surface",
    short: "Ink-free branding you can feel: a matched metal die lifts your logo out of the material, or presses it in.",
    intro:
      "Embossing and debossing are two sides of the same press. A matched metal die either lifts your logo up out of the material for a raised relief that catches the light, or presses it down into the surface for a quieter recessed impression. Both are completely ink-free and give leather notebooks, premium folders, gift boxes and thick cotton business cards a finish you can feel as well as see — and both can be combined with foil for a metallic result.",
    image: embossDebossImg,
    thumb: embossDebossThumb,
    icon: "IoLayersOutline",
    bestFor: ["Leather diaries & notebooks", "Business card holders", "Gift boxes & packaging", "Wallets, folders & portfolios", "Premium stationery", "Certificates & invitations"],
    advantages: [
      "Sophisticated, tactile finish",
      "Ink-free, tone-on-tone elegance",
      "Raised or recessed from one die set",
      "Adds perceived premium value",
      "Pairs beautifully with foil",
    ],
    process: [
      { title: "Die Creation", description: "A custom metal die and counter-die are made from your logo." },
      { title: "Setup", description: "The dies are aligned and registered to the item in the press." },
      { title: "Pressing", description: "Heat and pressure raise the design out of, or press it into, the surface." },
      { title: "Finishing", description: "Optional foil or colour fill is applied and each piece is checked." },
    ],
  },
  {
    slug: "id-card-printing",
    name: "ID Card Printing",
    tagline: "Staff, visitor and access cards — printed and encoded",
    short: "Full-colour PVC ID cards for employees, visitors and members, supplied with matching lanyards and holders.",
    intro:
      "We produce complete ID card solutions: full-colour PVC staff cards, visitor passes, membership and access cards, printed edge to edge and finished with the security features you need. Cards can be encoded with QR codes, barcodes, magnetic stripes or RFID chips, and because we also make lanyards, reel badges and card holders in-house, the whole set arrives matched and ready to issue.",
    image: idCardImg,
    thumb: idCardThumb,
    icon: "IoCardOutline",
    bestFor: ["Employee & staff ID cards", "Visitor & event passes", "Membership & loyalty cards", "RFID & access control cards", "School and campus IDs", "Matching lanyards & holders"],
    advantages: [
      "Edge-to-edge full-colour PVC printing",
      "QR, barcode, magnetic stripe & RFID encoding",
      "Individual photos and data per card",
      "Lanyards, holders and reels from one supplier",
      "Fast reprints for new joiners",
    ],
    process: [
      { title: "Template Design", description: "We build a card template around your brand and data fields." },
      { title: "Data Merge", description: "Names, photos and ID numbers are merged onto each card." },
      { title: "Print & Encode", description: "Cards are printed, laminated and encoded as required." },
      { title: "Kitting", description: "Cards are paired with lanyards or holders and delivered." },
    ],
  },
  {
    slug: "custom-box-printing",
    name: "Custom Box Printing",
    tagline: "Branded gift boxes that make the unboxing count",
    short: "Rigid gift boxes, mailers, sleeves and inserts printed in your brand colours with premium finishing.",
    intro:
      "The box is the first thing your client touches, so we treat it as part of the gift. We design and produce custom rigid boxes, mailer and shipping cartons, sleeves and inserts in your brand colours, finished with matt or gloss lamination, spot UV, foil stamping or embossing, and fitted with foam or card inserts that hold each item precisely in place — turning a set of gifts into a single considered package.",
    image: boxImg,
    thumb: boxThumb,
    icon: "IoCubeOutline",
    bestFor: ["Corporate gift boxes", "Product & retail packaging", "Mailer and shipping boxes", "Foam & card inserts", "Ramadan and Eid hampers", "Onboarding & welcome kits"],
    advantages: [
      "Made to the exact size of your gift set",
      "Foil, spot UV, lamination & embossing",
      "Rigid, folding and mailer constructions",
      "Custom inserts hold every item in place",
      "Packed and delivered ready to hand over",
    ],
    process: [
      { title: "Structure", description: "We size the box and insert around the items it has to hold." },
      { title: "Dieline & Artwork", description: "A cutting dieline is drawn and your artwork laid onto it." },
      { title: "Sample", description: "A physical mock-up is produced for approval before the run." },
      { title: "Production", description: "Boxes are printed, finished, die-cut, glued and assembled." },
    ],
  },
  {
    slug: "3d-printing",
    name: "3D Printing",
    tagline: "Bespoke shapes, prototypes and one-off pieces",
    short: "Custom three-dimensional objects built layer by layer — prototypes, models, awards and logo-shaped gifts.",
    intro:
      "3D printing builds an object layer by layer from a digital model, which makes genuinely custom shapes possible without the tooling costs of traditional manufacturing. We use it for rapid prototypes, architectural and product models, bespoke awards, and distinctive promotional pieces shaped around your logo — pieces that simply cannot be produced by printing onto an off-the-shelf item.",
    image: threeDImg,
    thumb: threeDThumb,
    icon: "IoCubeOutline",
    bestFor: ["Rapid prototypes", "Bespoke custom awards", "Architectural & product models", "Logo-shaped promotional items", "Display and exhibition props", "Small-batch custom parts"],
    advantages: [
      "Shapes no standard product can match",
      "No moulds or tooling investment",
      "Design changes between every print",
      "Viable from a single unit",
      "A range of finishes and materials",
    ],
    process: [
      { title: "3D Model", description: "We model your object, or prepare the file you supply, for print." },
      { title: "Slicing", description: "The model is sliced with the right layer height and supports." },
      { title: "Printing", description: "The piece is built layer by layer on the printer bed." },
      { title: "Finishing", description: "Supports are removed and the surface sanded, primed or painted." },
    ],
  },
];

export const getServiceBySlug = (slug?: string) =>
  services.find((s) => s.slug === slug);

/**
 * Per-service SEO copy from the client's keyword research.
 * Titles/descriptions are final — used verbatim on each /services/:slug page.
 */
export const serviceSeo: Record<string, { title: string; description: string; keyword: string }> = {
  "uv-printing": {
    title: "UV Printing Services Dubai | Vivid Full-Colour Logo Print",
    description:
      "Professional UV printing in Dubai for corporate gifts & merchandise. Full-colour, durable results on any surface. Bulk orders welcome. Fast turnaround.",
    keyword: "UV printing services Dubai",
  },
  "screen-printing": {
    title: "Screen Printing Dubai | Bulk Logo Printing on Merchandise",
    description:
      "Screen printing in Dubai for branded merchandise, corporate gifts & promo items. Vibrant, long-lasting colour results. Bulk orders welcome across the UAE.",
    keyword: "screen printing Dubai",
  },
  "digital-dtf-printing": {
    title: "DTF Printing Dubai | Digital & Direct-to-Film Print UAE",
    description:
      "DTF & digital printing in Dubai — no minimums, edge-to-edge full colour. Perfect for custom apparel, bags & branded gifts. Photo-quality finish, no peeling.",
    keyword: "DTF printing Dubai",
  },
  "heat-transfer-vinyl": {
    title: "Heat Transfer & Vinyl Printing UAE | Apparel Branding",
    description:
      "Heat transfer & vinyl printing in UAE for branded uniforms, t-shirts & corporate apparel. Sharp, durable logo application. Fast turnaround across Dubai.",
    keyword: "heat transfer printing apparel UAE",
  },
  "sublimation-printing": {
    title: "Sublimation Printing UAE | Photo-Quality Gift Printing",
    description:
      "Sublimation printing in UAE for mugs, bottles, apparel & corporate gifts. Edge-to-edge full-colour photo quality, no peeling. No minimums. Fast Dubai delivery.",
    keyword: "sublimation printing UAE",
  },
  "laser-engraving": {
    title: "Laser Engraving Dubai | Premium Corporate Gift Marking",
    description:
      "Laser engraving in Dubai for corporate gifts & executive awards. Permanent, ink-free marking on metal, wood, leather & more. Fast UAE-wide delivery.",
    keyword: "laser engraving Dubai",
  },
  embroidery: {
    title: "Custom Embroidery Dubai | Logo Stitching on Uniforms UAE",
    description:
      "Custom embroidery in Dubai for branded uniforms, caps, bags & corporate apparel. High-quality stitched logos with precise colour matching across the UAE.",
    keyword: "custom embroidery Dubai",
  },
  "embossing-debossing": {
    title: "Embossing & Debossing Dubai | Raised & Recessed Branding",
    description:
      "Embossing and debossing in Dubai for corporate gifts, leather notebooks, folders and packaging. Elegant, ink-free raised or recessed logo branding.",
    keyword: "embossing services Dubai",
  },
  "360-uv-printing": {
    title: "360° UV Printing Dubai | Wrap-Around Bottle & Tumbler Print",
    description:
      "Seamless 360° UV printing in Dubai for bottles, tumblers and cylindrical corporate gifts. Full-colour wrap-around branding with no visible join.",
    keyword: "360 UV printing Dubai",
  },
  "digital-printing": {
    title: "Digital Printing Dubai | Fast Short-Run & Personalised Print",
    description:
      "Digital printing in Dubai — fast, accurate short runs of business cards, flyers, brochures and certificates with variable-data personalisation.",
    keyword: "digital printing Dubai",
  },
  "mug-sublimation-printing": {
    title: "Mug Printing Dubai | Custom Sublimation Mugs & Cups UAE",
    description:
      "Custom mug printing in Dubai — dishwasher-safe full-colour photo mugs, magic mugs and branded ceramic cups. No minimums, fast UAE-wide delivery.",
    keyword: "mug printing Dubai",
  },
  "laser-marking": {
    title: "Laser Marking Dubai | Serial Number, QR & Barcode Marking",
    description:
      "Permanent laser marking in Dubai for serial numbers, QR codes, barcodes and fine detail on metal, plastic and technical parts. Precise and repeatable.",
    keyword: "laser marking Dubai",
  },
  "id-card-printing": {
    title: "ID Card Printing Dubai | Staff, Visitor & RFID Access Cards",
    description:
      "Professional ID card printing in Dubai — full-colour PVC staff, visitor and RFID access cards supplied with matching lanyards and card holders.",
    keyword: "ID card printing Dubai",
  },
  "custom-box-printing": {
    title: "Custom Box Printing Dubai | Branded Gift & Product Packaging",
    description:
      "Custom printed boxes and packaging in Dubai — rigid gift boxes, mailers, sleeves and inserts with foil, spot UV, lamination and embossing.",
    keyword: "custom box printing Dubai",
  },
  "3d-printing": {
    title: "3D Printing Dubai | Prototypes, Models & Custom Awards",
    description:
      "3D printing services in Dubai for rapid prototypes, architectural models, bespoke awards and custom logo-shaped promotional items.",
    keyword: "3D printing Dubai",
  },
};
